/**
 * Drag-to-start + mode select (owner's start-flow spec).
 *
 * This scene is mounted BEHIND the React menu while the app is on the start
 * screen, so the live, teased player ball shows through the glass UI. The ball
 * gently bobs and its googly eyes orbit, inviting a drag.
 *
 * The player STARTS THE RUN BY DRAGGING THE BALL. The instant a drag begins we
 * emit a one-shot {@link import('./events').RunStartPayload} on the bus so React
 * can elegantly animate its menu away and reveal the game. Whichever SIDE the
 * pointer then dwells on picks the mode — LEFT = solo, RIGHT = multiplayer: a
 * short dwell timer fills while the pointer sits on a side; once it fills the mode
 * locks and we transition into {@link MainScene}.
 *
 * FALLBACK: a `start` bus command (the menu's Play button) commits solo
 * immediately, so the game is always playable even without the drag gesture.
 *
 * Multiplayer is a PLACEHOLDER: choosing it shows a brief "Multiplayer — coming
 * soon" message, then falls back to solo (the netcode lands in a later phase).
 *
 * This scene owns NO game rules and no core sim — it's a human-timescale chooser.
 * On commit it adds a fresh {@link MainScene} with the chosen {@link GameMode} and
 * starts it (removing itself), so MainScene boots exactly as before but with mode.
 */
import Phaser from 'phaser';
import type { GameMode } from '../core';
import { PLAYER, CANONICAL_VIEWPORT } from '../core';
import { MainScene } from './MainScene';
import { Player } from './Player';
import { Background } from './Background';
import { getSkin } from './skins';
import { fitCameraToCanonical, alignHud } from './viewport';
import type { GameBus, GameCommand } from './events';

/** How long (ms) the pointer must dwell on a side before that mode commits. */
const DWELL_MS = 700;
/** How far (px) from centre counts as "on a side" (ignores tiny jitters at rest). */
const SIDE_DEADZONE = 48;
/** How long (ms) to show the "coming soon" notice before falling back to solo. */
const COMING_SOON_MS = 1100;
/** Idle bob: vertical amplitude (px) and angular speed (rad/s) of the teased ball. */
const BOB_AMPLITUDE = 10;
const BOB_SPEED = 1.6;
/**
 * Vertical resting position of the teased ball, as a FRACTION of viewport height.
 * Lifted above dead-centre (owner request) so the ball + "Drag to start" hint sit
 * in a clear upper band, well above the bottom controls cluster (stats / POPSTAR /
 * leaderboard) that previously crowded the centred ball. The React `.drag-teaser`
 * is pinned to the same fraction in src/index.css so the hint frames the ball.
 */
const BALL_Y_FRACTION = 0.4;

type Side = 'left' | 'right' | null;

export class StartFlowScene extends Phaser.Scene {
  private readonly bus: GameBus;
  private readonly skinId: string;

  private player!: Player;
  private background!: Background;
  private homeX = 0;
  private homeY = 0;

  /** Upright HUD overlay (side labels) over the zoomed/rotated world camera. */
  private hud!: Phaser.GameObjects.Container;
  /** Subtle in-canvas side affordances (the rich UI is the React menu on top). */
  private soloLabel!: Phaser.GameObjects.Text;
  private multiLabel!: Phaser.GameObjects.Text;
  private noticeText!: Phaser.GameObjects.Text;

  private dragging = false;
  private side: Side = null;
  private dwellMs = 0;
  private committed = false;
  /** Guard so `runstart` is emitted exactly once per scene lifetime. */
  private runStartEmitted = false;
  private bobPhase = 0;
  private commandUnsub: (() => void) | null = null;
  /** Guard so the external-listener cleanup runs exactly once (SHUTDOWN or DESTROY). */
  private cleanedUp = false;

  constructor(bus: GameBus, skinId: string) {
    super({ key: 'startflow' });
    this.bus = bus;
    this.skinId = skinId;
  }

  create(): void {
    const font = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
    // Same canonical field + camera as the live game (so the teased ball, the drag,
    // and the committed start position are all in canonical coords).
    const vw = CANONICAL_VIEWPORT.width;
    const vh = CANONICAL_VIEWPORT.height;
    fitCameraToCanonical(this);
    const cx = vw / 2;
    const ballY = vh * BALL_Y_FRACTION;
    const { width: cw, height: ch } = this.scale.gameSize;

    // Arena backdrop, sized to the canonical field (purely decorative).
    this.background = new Background(this);
    this.background.resize(vw, vh);

    // The live, teased ball the player drags to start (canonical coords). Bobs idly.
    this.homeX = cx;
    this.homeY = ballY;
    this.player = new Player(this, cx, ballY, getSkin(this.skinId));

    // Faint side affordances live in an upright HUD container (screen px). The React
    // menu carries the real call-to-action, so these are understated + fade on drag.
    this.hud = this.add.container(0, 0).setDepth(25);
    this.soloLabel = this.add
      .text(cw * 0.14, ch * BALL_Y_FRACTION, '◀ SOLO', {
        fontFamily: font,
        fontSize: '20px',
        color: '#4cc9f0',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0.0);
    this.multiLabel = this.add
      .text(cw * 0.86, ch * BALL_Y_FRACTION, 'MULTI ▶', {
        fontFamily: font,
        fontSize: '20px',
        color: '#b388ff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0.0);

    this.noticeText = this.add
      .text(cw / 2, ch * (BALL_Y_FRACTION + 0.22), '', {
        fontFamily: font,
        fontSize: '20px',
        color: '#ffd166',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setVisible(false);
    this.hud.add([this.soloLabel, this.multiLabel, this.noticeText]);
    alignHud(this, this.hud);

    this.input.on('pointerdown', this.onDown, this);
    this.input.on('pointermove', this.onMove, this);
    this.input.on('pointerup', this.onUp, this);
    this.scale.on('resize', this.onResize, this);

    // The Play-button fallback (React → bus) commits solo without a drag.
    this.commandUnsub?.();
    this.commandUnsub = this.bus.onCommand(this.onCommand);
    this.cleanedUp = false;
    // Clean up on SHUTDOWN (hand-off to MainScene) AND DESTROY — navigating menu →
    // network remounts the Phaser.Game, and game.destroy() fires only DESTROY, so
    // without this the app-lifetime GameBus subscription would leak.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown, this);
    this.events.once(Phaser.Scenes.Events.DESTROY, this.onShutdown, this);
  }

  private onResize = (gameSize: Phaser.Structs.Size): void => {
    // The teased ball lives in the fixed canonical field; only the camera + HUD re-fit.
    fitCameraToCanonical(this);
    if (this.hud) {
      alignHud(this, this.hud);
      const { width: cw, height: ch } = gameSize;
      this.soloLabel.setPosition(cw * 0.14, ch * BALL_Y_FRACTION);
      this.multiLabel.setPosition(cw * 0.86, ch * BALL_Y_FRACTION);
      this.noticeText.setPosition(cw / 2, ch * (BALL_Y_FRACTION + 0.22));
    }
  };

  private onCommand = (cmd: GameCommand): void => {
    // Only `start` is meaningful here (the Play fallback). Other commands belong
    // to MainScene and are ignored before a run begins.
    if (cmd === 'start' && !this.committed) {
      this.beginRun(null);
      this.commit('solo');
    }
  };

  private onDown = (pointer: Phaser.Input.Pointer): void => {
    if (this.committed) return;
    this.dragging = true;
    this.beginRun(this.resolveSide(pointer));
    this.updateSide(pointer);
  };

  private onMove = (pointer: Phaser.Input.Pointer): void => {
    if (!this.dragging || this.committed) return;
    this.player.setPosition(pointer.worldX, pointer.worldY - PLAYER.pointerOffsetY);
    this.updateSide(pointer);
  };

  private onUp = (): void => {
    // Releasing abandons the in-progress dwell (the dwell must be sustained), but
    // the run has already begun — the ball glides back home and keeps teasing.
    this.dragging = false;
    this.side = null;
    this.dwellMs = 0;
  };

  /**
   * Fire the one-shot `runstart` boundary signal (idempotent) and reveal the
   * in-canvas side affordances so the chooser reads clearly once React's menu
   * animates away.
   */
  private beginRun(side: Side): void {
    if (this.runStartEmitted) return;
    this.runStartEmitted = true;
    this.bus.emitRunStart({ side });
    this.tweens.add({ targets: [this.soloLabel, this.multiLabel], alpha: 1, duration: 220 });
  }

  /** Which side the pointer is on (or null in the centre deadzone). */
  private resolveSide(pointer: Phaser.Input.Pointer): Side {
    const cx = this.scale.gameSize.width / 2;
    const dx = pointer.x - cx;
    return dx < -SIDE_DEADZONE ? 'left' : dx > SIDE_DEADZONE ? 'right' : null;
  }

  private updateSide(pointer: Phaser.Input.Pointer): void {
    const next = this.resolveSide(pointer);
    if (next !== this.side) {
      this.side = next;
      this.dwellMs = 0; // restart the dwell when the side changes
    }
  }

  override update(_time: number, delta: number): void {
    this.background.update(Math.min(delta, 100) / 1000);
    if (this.committed) return;

    // Idle tease: while not being dragged, bob the resting ball and orbit its eyes.
    if (!this.dragging) {
      this.bobPhase = (this.bobPhase + (delta / 1000) * BOB_SPEED) % (Math.PI * 2);
      const y = this.homeY + Math.sin(this.bobPhase) * BOB_AMPLITUDE;
      this.player.setPosition(this.homeX, y);
    }
    // Advance the googly face every frame (idle orbit; no danger on the menu).
    this.player.update(delta / 1000, null, PLAYER.eyeLookRange);

    // Sustained dwell on a side commits the mode.
    if (this.dragging && this.side) {
      this.dwellMs += delta;
      if (this.dwellMs >= DWELL_MS) {
        this.commit(this.side === 'left' ? 'solo' : 'multiplayer');
      }
    } else {
      this.dwellMs = 0;
    }
  }

  /**
   * Lock the chosen mode and transition into {@link MainScene}. Multiplayer is a
   * placeholder — we flash "coming soon" then fall back to solo.
   */
  private commit(mode: GameMode): void {
    if (this.committed) return;
    this.committed = true;

    if (mode === 'multiplayer') {
      this.noticeText.setText('Multiplayer — coming soon. Starting solo…').setVisible(true);
      this.time.delayedCall(COMING_SOON_MS, () => this.startGame('solo'));
      return;
    }
    this.startGame('solo');
  }

  /** Hand off to MainScene with the chosen mode (this scene removes itself). */
  private startGame(mode: GameMode): void {
    const key = 'main';
    // Replace any prior MainScene instance so it boots fresh with the chosen mode.
    if (this.scene.get(key)) this.scene.remove(key);
    // Pass the ball's current position so MainScene starts the run there (no snap to
    // centre) — the player flows straight from the drag into the run.
    this.scene.add(key, new MainScene(this.bus, this.skinId, mode, this.player.x, this.player.y), false);
    this.scene.start(key);
  }

  private onShutdown = (): void => {
    if (this.cleanedUp) return; // idempotent (may fire on both SHUTDOWN and DESTROY)
    this.cleanedUp = true;
    this.scale.off('resize', this.onResize, this);
    this.commandUnsub?.();
    this.commandUnsub = null;
    this.player?.destroy();
    this.background?.destroy();
  };
}
